import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, NavigationStart, Router } from '@angular/router';
import { Subject, catchError, debounceTime, filter, forkJoin, map, of, takeUntil } from 'rxjs';
import { ICompany } from '../companies/models/company.interface';
import { CompanyService } from '../companies/services/company.service';
import { CustomQuestionnaireService } from '../questionarios/services/custom-questionnaire.service';
import { ICustomQuestionnaire } from '../setups/models/questionnaire.interface';
import { SkincareQuestionnaireService } from '../questionario-skincare/services/skincare-questionnaire.service';
import { ISkincareQuestion } from '../questionario-skincare/models/skincare-question.interface';
import {
  CatalogoProdutosImportService,
  ICatalogFileProcessingResult
} from './services/catalogo-produtos-import.service';
import { CatalogListResponse, CatalogoProdutosService } from './services/catalogo-produtos.service';
import { CatalogoProdutosQuestionMapperService } from './services/catalogo-produtos-question-mapper.service';
import { CatalogoProdutosStateService } from './services/catalogo-produtos-state.service';
import { SessionService } from '../../shared/services/session.service';
import { ToastService } from '../../shared/services/toast.service';
import { CATALOG_CATEGORY_OPTIONS, CATALOG_HEADER_ALIASES, CATALOG_ROUTINE_OPTIONS } from './constants';
import {
  getCatalogPriceLabel,
  getCatalogWeightLabel,
  getCatalogWeightRangeValue,
  getFirstFilledCatalogValue,
  isCatalogRangeValid,
  isCatalogPriceValid,
  isCatalogWeightValid,
  mapCatalogCategoriesToCategory,
  mapCatalogRangeToString,
  mapCatalogRoutineToRoutineInformation,
  mapCatalogPriceToString,
  mapCatalogWeightToString,
  normalizeCatalogHeader,
  rowHasCatalogData
} from './utils/catalogo-produtos.utils';
import { CatalogMode, ICatalogRow, ISkincareThresholdSettings, IServerCatalogItem } from './models/catalog';
import { IKitMatrix } from '../motor-kits-skincare/models/kit-matrix.interface';
import { ERoleAdminString } from 'src/app/shared/data/role.enum';

@Component({
  selector: 'app-catalogo-produtos',
  templateUrl: './catalogo-produtos.component.html',
  styleUrls: ['./catalogo-produtos.component.scss']
})
export class CatalogoProdutosComponent implements OnInit, OnDestroy {
  ERoleAdminString = ERoleAdminString;
  form = new UntypedFormGroup({
    companyId: new UntypedFormControl(null, [Validators.required]),
    file: new UntypedFormControl(null, [Validators.required])
  });
  mode: CatalogMode = 'upload';

  companies: ICompany[] = [];
  fileName = '';
  previewHeaders: string[] = [];
  previewColumns: string[] = [];
  previewRows: string[][] = [];
  previewTotalRows = 0;
  previewMessage = '';
  isUploading = false;
  isLoadingExistingCatalog = false;
  isCatalogLoadedFromServer = false;
  loadedCatalogCompanyId: string | null = null;
  catalogLoadMessage = '';
  readonly maxPreviewRows = 5;
  readonly maxPreviewColumns = 6;
  readonly minWeight = 0;
  readonly maxWeight = 100;
  readonly headerAliases = CATALOG_HEADER_ALIASES;
  readonly routineOptions = CATALOG_ROUTINE_OPTIONS;
  readonly categoryOptions = CATALOG_CATEGORY_OPTIONS;
  questions: ISkincareQuestion[] = [];
  manualRows: ICatalogRow[] = [];
  importedRows: ICatalogRow[] = [];
  searchProductName = '';
  selectedRowIndex: number | null = null;
  paginaSelecionada = 0;
  tamanhoPagina = 10;
  totalPaginas = 1;
  isCreatingProduct = false;
  catalogSource: 'company' | null = null;
  userActivatedMode = false;
  thresholdSettings: ISkincareThresholdSettings | null = null;
  kitMatrix: IKitMatrix | null = null;

  private readonly draftStorageKeyPrefix = 'catalogo-produtos-draft-v1';
  private readonly draftPersistDebounceMs = 400;
  private readonly draftPersist$ = new Subject<void>();
  private readonly destroy$ = new Subject<void>();
  private shouldClearDraftOnDestroy = false;

  constructor(
    private readonly companyService: CompanyService,
    private readonly catalogoProdutosImportService: CatalogoProdutosImportService,
    private readonly catalogoProdutosQuestionMapperService: CatalogoProdutosQuestionMapperService,
    private readonly catalogoProdutosService: CatalogoProdutosService,
    private readonly catalogoProdutosStateService: CatalogoProdutosStateService,
    private readonly customQuestionnaireService: CustomQuestionnaireService,
    private readonly skincareQuestionnaireService: SkincareQuestionnaireService,
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly sessionService: SessionService,
    private readonly toastService: ToastService
  ) {}

  ngOnInit(): void {
    this.companyService.listarEmpresasAtivas().subscribe((response) => {
      this.companies = response.data || [];
    });

    const companyId = this.sessionService.getSession()?.companyId;
    if (companyId) {
      this.form.get('companyId')?.setValue(companyId);
      this.form.get('companyId')?.disable();
    }

    this.restoreDraftState();

    // Cobre tanto a empresa fixa da sessão quanto uma empresa restaurada do draft.
    const companyIdAtual = this.form.getRawValue().companyId;
    if (companyIdAtual) {
      this.loadSkincareQuestions(String(companyIdAtual));
      this.loadExistingCatalog();
    }

    const routeMode = this.resolveModeFromRoutePath(this.getCurrentRoutePath());
    if (routeMode && !this.shouldPreserveRestoredManualMode(routeMode)) {
      this.mode = routeMode;
    }

    if (!this.manualRows.length) {
      this.manualRows = [this.createEmptyRow()];
    }
    this.selectedRowIndex = null;
    this.isCreatingProduct = false;
    this.applyFileControlState(this.mode);
    this.updatePagination();
    this.watchDraftPersistence();
    this.watchPageExit();
    this.watchRouteModeChanges();
    this.syncModeRoute(this.mode);
    this.persistDraftStateNow();
  }

  ngOnDestroy(): void {
    if (this.shouldClearDraftOnDestroy) {
      this.clearDraftState();
    } else {
      this.persistDraftStateNow();
    }

    this.destroy$.next();
    this.destroy$.complete();
  }

  @HostListener('window:beforeunload')
  onBeforeUnload(): void {
    this.shouldClearDraftOnDestroy = true;
    this.clearDraftState();
  }

  @HostListener('input')
  onInputChange(): void {
    this.persistDraftState();
  }

  @HostListener('change')
  onChange(): void {
    this.persistDraftState();
  }

  @HostListener('document:keydown.escape')
  onEscapeKey(): void {
    if (this.activeRow) {
      this.clearEdit();
    }
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];

    if (!file) {
      this.persistDraftState();
      return;
    }

    this.form.get('file')?.setValue(file);
    this.fileName = file.name;
    this.resetPreview();
    this.catalogoProdutosImportService
      .processSelectedFile(file, {
        maxPreviewRows: this.maxPreviewRows,
        maxPreviewColumns: this.maxPreviewColumns,
        headerAliases: this.headerAliases,
        questions: this.questions
      })
      .then((result) => {
        this.applyFileProcessingResult(result);
        if (!result.isSupported) {
          this.form.get('file')?.reset();
          this.fileName = '';
        }
        this.persistDraftState();
      })
      .catch(() => {
        this.previewMessage = 'Nao foi possivel ler o arquivo selecionado.';
        this.persistDraftState();
      });
  }

  setMode(mode: CatalogMode): void {
    if (this.mode === mode) {
      this.syncModeRoute(mode);
      this.persistDraftState();
      return;
    }

    this.mode = mode;
    this.resetLoadedCatalogState();
    this.resetPreview();
    this.previewMessage = '';

    if (mode === 'manual') {
      if (!this.manualRows.length) {
        this.manualRows = [this.createEmptyRow()];
      }
    }

    this.applyFileControlState(mode);
    this.paginaSelecionada = 0;
    this.selectedRowIndex = null;
    this.isCreatingProduct = false;
    this.updatePagination();
    this.syncModeRoute(mode);
    this.persistDraftState();
  }

  private applyFileControlState(mode: CatalogMode): void {
    const fileControl = this.form.get('file');
    if (!fileControl) {
      return;
    }

    if (mode === 'upload') {
      fileControl.setValidators([Validators.required]);
      fileControl.updateValueAndValidity({ emitEvent: false });
      return;
    }

    fileControl.reset();
    this.fileName = '';
    fileControl.clearValidators();
    fileControl.updateValueAndValidity({ emitEvent: false });
  }

  private resolveModeFromRoutePath(routePath: string | null | undefined): CatalogMode | null {
    if (routePath === 'manual') {
      return 'manual';
    }
    if (routePath === 'importar') {
      return 'upload';
    }
    return null;
  }

  private modeToRoutePath(mode: CatalogMode): string {
    return mode === 'manual' ? 'manual' : 'importar';
  }

  private getCurrentRoutePath(): string {
    return this.route.snapshot.url[0]?.path || this.route.snapshot.routeConfig?.path || '';
  }

  private syncModeRoute(mode: CatalogMode): void {
    const targetPath = this.modeToRoutePath(mode);
    if (this.getCurrentRoutePath() === targetPath) {
      return;
    }

    this.router.navigate(['../', targetPath], {
      relativeTo: this.route,
      replaceUrl: true
    });
  }

  private watchRouteModeChanges(): void {
    this.route.url.pipe(takeUntil(this.destroy$)).subscribe((segments) => {
      const routePath = segments[0]?.path || this.route.snapshot.routeConfig?.path || '';
      const routeMode = this.resolveModeFromRoutePath(routePath);
      if (!routeMode || routeMode === this.mode) {
        return;
      }

      this.setMode(routeMode);
    });
  }

  private watchPageExit(): void {
    this.router.events
      .pipe(
        filter((event): event is NavigationStart => event instanceof NavigationStart),
        takeUntil(this.destroy$)
      )
      .subscribe((event) => {
        const isCatalogRoute = this.isCatalogRoute(event.url);
        this.shouldClearDraftOnDestroy = !isCatalogRoute;

        if (this.shouldClearDraftOnDestroy) {
          this.clearDraftState();
        }
      });
  }

  private watchDraftPersistence(): void {
    this.draftPersist$.pipe(debounceTime(this.draftPersistDebounceMs), takeUntil(this.destroy$)).subscribe(() => {
      if (this.shouldClearDraftOnDestroy) {
        return;
      }

      this.persistDraftStateNow();
    });
  }

  private persistDraftState(): void {
    if (this.shouldClearDraftOnDestroy) {
      return;
    }

    this.draftPersist$.next();
  }

  private persistDraftStateNow(): void {
    this.catalogoProdutosStateService.persistDraftState(this.getDraftStorageKey(), {
      mode: this.mode,
      companyId: this.form.getRawValue().companyId,
      searchProductName: this.searchProductName,
      manualRows: this.manualRows,
      importedRows: this.importedRows,
      paginaSelecionada: this.paginaSelecionada,
      tamanhoPagina: this.tamanhoPagina,
      isCatalogLoadedFromServer: this.isCatalogLoadedFromServer,
      loadedCatalogCompanyId: this.loadedCatalogCompanyId,
      catalogLoadMessage: this.catalogLoadMessage
    });
  }

  private restoreDraftState(): void {
    const restoredState = this.catalogoProdutosStateService.restoreDraftState(this.getDraftStorageKey());
    if (!restoredState) {
      return;
    }

    if (restoredState.mode) {
      this.mode = restoredState.mode;
    }

    this.manualRows = restoredState.manualRows;
    this.importedRows = restoredState.importedRows;
    this.paginaSelecionada = restoredState.paginaSelecionada;

    if (restoredState.tamanhoPagina !== null) {
      this.tamanhoPagina = restoredState.tamanhoPagina;
    }

    this.isCatalogLoadedFromServer = restoredState.isCatalogLoadedFromServer;
    this.loadedCatalogCompanyId = restoredState.loadedCatalogCompanyId;
    this.catalogLoadMessage = restoredState.catalogLoadMessage;
    this.searchProductName = restoredState.searchProductName;

    const companyControl = this.form.get('companyId');
    if (restoredState.companyId && companyControl?.enabled) {
      companyControl.setValue(restoredState.companyId);
    }
  }

  private getDraftStorageKey(): string {
    const session = this.sessionService.getSession();
    const sid = String(session?.sid || 'anonymous');
    const companyId = String(session?.companyId || 'no-company');
    return `${this.draftStorageKeyPrefix}:${sid}:${companyId}`;
  }

  private clearDraftState(): void {
    this.catalogoProdutosStateService.removeDraftState(this.getDraftStorageKey());
  }

  private isCatalogRoute(url: string): boolean {
    const normalizedUrl = (url || '').split('?')[0].split('#')[0];
    return normalizedUrl === '/catalogo-produtos' || normalizedUrl.startsWith('/catalogo-produtos/');
  }

  onCompanySidebarSelected(companyId: string): void {
    this.form.get('companyId')?.setValue(companyId);
    this.onCompanySelectionChanged(companyId);
  }

  onCompanySelectionChanged(companyId: string | null): void {
    // As questões precisam estar carregadas antes de montar o catálogo manualmente,
    // não só ao carregar um catálogo existente do servidor.
    this.loadSkincareQuestions(companyId);

    if (companyId && companyId === this.loadedCatalogCompanyId) {
      return;
    }

    this.resetLoadedCatalogState();
    this.manualRows = [this.createEmptyRow()];
    this.searchProductName = '';
    this.selectedRowIndex = null;
    this.isCreatingProduct = false;
    this.paginaSelecionada = 0;
    this.updatePagination();
    this.persistDraftState();
    this.catalogSource = null;
    this.userActivatedMode = false;
    this.thresholdSettings = null;
    this.kitMatrix = null;
    if (companyId) {
      this.loadExistingCatalog(String(companyId));
    }
  }

  private loadSkincareQuestions(companyId: string | null): void {
    if (!companyId) {
      this.questions = [];
      return;
    }

    this.skincareQuestionnaireService
      .getActive(String(companyId))
      .pipe(
        catchError(() => of([] as ISkincareQuestion[])),
        takeUntil(this.destroy$)
      )
      .subscribe((questions) => {
        this.questions = this.sortQuestions(questions ?? []);
      });
  }

  loadExistingCatalog(companyIdOverride?: string): void {
    const rawValue = this.form.getRawValue();
    const companyId = companyIdOverride ?? rawValue.companyId;
    if (!companyId) {
      this.toastService.toast('Selecione uma empresa para carregar o catálogo.');
      return;
    }

    const id = String(companyId);
    this.isLoadingExistingCatalog = true;
    this.isCatalogLoadedFromServer = false;
    this.loadedCatalogCompanyId = null;
    this.userActivatedMode = false;
    this.catalogLoadMessage = '';

    forkJoin({
      catalogResponse: this.catalogoProdutosService.listarCatalogoPorCompanyId(id),
      questionnaires: this.customQuestionnaireService
        .getByCompanyId(id)
        .pipe(catchError(() => of([] as ICustomQuestionnaire[]))),
      // Precisa ser o questionário DA EMPRESA: sem o companyId a API devolve o global,
      // e o mapeamento sairia apontando para opções que não existem no questionário dela.
      skincareQuestions: this.skincareQuestionnaireService
        .getActive(id)
        .pipe(catchError(() => of([] as ISkincareQuestion[]))),
      thresholdSettings: this.catalogoProdutosService.listarThresholdsPorCompanyId(id).pipe(
        map((response) => response?.data ?? null),
        catchError(() => of(null))
      ),
      kitMatrix: this.catalogoProdutosService.listarKitMatrix(id).pipe(
        map((response) => response?.data ?? null),
        catchError(() => of(null))
      )
    }).subscribe({
      next: ({ catalogResponse, questionnaires, skincareQuestions, thresholdSettings, kitMatrix }) => {
        this.questions = this.sortQuestions(skincareQuestions);
        this.thresholdSettings = thresholdSettings;
        this.kitMatrix = kitMatrix;
        this.catalogoProdutosQuestionMapperService.cacheQuestionnaireAnswerLabels(questionnaires);
        const existingItems = this.extractCatalogItems(catalogResponse.data);
        const rows = existingItems.map((item) => this.mapServerItemToRow(item)).filter((row) => this.rowHasData(row));

        this.mode = 'manual';
        this.previewMessage = '';
        this.applyFileControlState('manual');
        this.manualRows = rows.length ? rows : [this.createEmptyRow()];
        this.searchProductName = '';
        this.selectedRowIndex = null;
        this.isCreatingProduct = false;
        this.paginaSelecionada = 0;
        this.updatePagination();

        if (!rows.length) {
          this.resetLoadedCatalogState();
          this.catalogSource = null;
          this.catalogLoadMessage = 'Nenhum produto foi retornado para essa empresa.';
          this.toastService.toast('Nenhum catálogo foi encontrado para a empresa selecionada.');
          this.isLoadingExistingCatalog = false;
          this.persistDraftState();
          return;
        }

        this.isCatalogLoadedFromServer = true;
        this.loadedCatalogCompanyId = id;
        this.catalogSource = 'company';
        this.catalogLoadMessage = `${rows.length} produto(s) carregado(s)`;
        this.toastService.toast('Catálogo carregado com sucesso.');
        this.isLoadingExistingCatalog = false;
        this.persistDraftState();
      },
      error: () => {
        this.resetLoadedCatalogState();
        this.catalogSource = null;
        this.thresholdSettings = null;
        this.kitMatrix = null;
        this.catalogLoadMessage = 'Nao foi possivel carregar o catálogo existente.';
        this.isLoadingExistingCatalog = false;
        this.persistDraftState();
      }
    });
  }

  addRow(): void {
    this.manualRows = [...this.manualRows, this.createEmptyRow()];
    this.selectedRowIndex = this.manualRows.length - 1;
    this.isCreatingProduct = true;
    this.updatePagination();
    this.persistDraftState();
  }

  removeRow(index: number): void {
    if (index < 0 || index >= this.currentRows.length) {
      return;
    }

    if (this.mode === 'manual') {
      this.manualRows = this.manualRows.filter((_, rowIndex) => rowIndex !== index);
      if (!this.manualRows.length) {
        this.manualRows = [this.createEmptyRow()];
        this.selectedRowIndex = null;
        this.isCreatingProduct = false;
        this.updatePagination();
        this.persistDraftState();
        return;
      }
    } else {
      this.importedRows = this.importedRows.filter((_, rowIndex) => rowIndex !== index);
      if (!this.importedRows.length) {
        this.selectedRowIndex = null;
        this.isCreatingProduct = false;
        this.updatePagination();
        this.persistDraftState();
        return;
      }
    }

    if (this.selectedRowIndex === index) {
      this.selectedRowIndex = null;
      this.isCreatingProduct = false;
      this.updatePagination();
      this.persistDraftState();
      return;
    }

    if (this.selectedRowIndex !== null && this.selectedRowIndex > index) {
      this.selectedRowIndex -= 1;
    }

    this.updatePagination();
    this.persistDraftState();
  }

  editRow(index: number): void {
    if (index < 0 || index >= this.currentRows.length) {
      return;
    }
    this.selectedRowIndex = index;
    this.isCreatingProduct = false;
    this.persistDraftState();
  }

  clearEdit(): void {
    this.selectedRowIndex = null;
    this.isCreatingProduct = false;
    this.persistDraftState();
  }

  onDialogSaved(row: ICatalogRow): void {
    if (this.selectedRowIndex !== null) {
      this.currentRows[this.selectedRowIndex] = row;
    }
    this.selectedRowIndex = null;
    this.isCreatingProduct = false;
    this.persistDraftState();
    this.updatePagination();
  }

  onDialogCancelled(): void {
    this.clearEdit();
  }

  activateMode(mode: 'upload' | 'manual'): void {
    this.userActivatedMode = true;
    this.setMode(mode);
  }

  get activeRow(): ICatalogRow | null {
    if (this.selectedRowIndex === null) {
      return null;
    }
    return this.currentRows[this.selectedRowIndex] || null;
  }

  uploadCatalog(): void {
    const { companyId, file } = this.form.getRawValue();

    if (!companyId) {
      this.toastService.toast('Selecione uma empresa para importar.');
      return;
    }

    if (this.mode === 'upload' && !file && !this.importedRows.length) {
      this.toastService.toast('Selecione um arquivo para importar.');
      return;
    }

    if (this.mode === 'manual' && !this.manualHasData()) {
      this.toastService.toast('Adicione pelo menos um produto antes de importar.');
      return;
    }

    // O CSV é montado a partir de this.questions: sem elas, buildCsvFileFromRows não
    // emite nenhuma coluna de pergunta e o mapeamento produto/resposta é descartado
    // em silêncio, gerando um catálogo que nunca pontua. Melhor barrar do que salvar vazio.
    if (!this.questions.length) {
      this.toastService.toast('Publique o questionário desta empresa antes de importar o catálogo.');
      return;
    }

    const rangeIssues = this.collectRangeIssues(this.currentRows);
    if (rangeIssues.length > 0) {
      const firstIssues = rangeIssues.slice(0, 3).map((issue) => `#${issue.index + 1}: ${issue.fields.join(', ')}`);
      const suffix = rangeIssues.length > 3 ? '...' : '';
      this.toastService.toast(`Ajuste os ranges inválidos: ${firstIssues.join(' | ')}${suffix}`);
      return;
    }

    const weightIssues = this.collectWeightIssues(this.currentRows);
    if (weightIssues.length > 0) {
      const firstIssues = weightIssues.slice(0, 5).map((index) => `#${index + 1}`);
      const suffix = weightIssues.length > 5 ? '...' : '';
      this.toastService.toast(
        `Ajuste o peso inválido nos produtos ${firstIssues.join(', ')}${suffix}. Use um número entre ${this.minWeight} e ${this.maxWeight}.`
      );
      return;
    }

    const priceIssues = this.collectPriceIssues(this.currentRows);
    if (priceIssues.length > 0) {
      const firstIssues = priceIssues.slice(0, 5).map((index) => `#${index + 1}`);
      const suffix = priceIssues.length > 5 ? '...' : '';
      this.toastService.toast(
        `Ajuste o preço inválido nos produtos ${firstIssues.join(', ')}${suffix}. Use um valor como 89,90.`
      );
      return;
    }

    const uploadFile =
      this.mode === 'manual'
        ? this.catalogoProdutosImportService.buildCsvFileFromRows(this.manualRows, this.questions)
        : this.catalogoProdutosImportService.buildUploadFileForImport(this.importedRows, file, this.questions);

    this.isUploading = true;
    this.catalogoProdutosService.importarCatalogo(uploadFile, String(companyId)).subscribe({
      next: (resposta) => {
        if (resposta?.messages?.length) {
          this.toastService.toast(resposta.messages);
        } else {
          this.toastService.toast(
            this.isCatalogLoadedFromServer ? 'Catálogo atualizado com sucesso.' : 'Catálogo importado com sucesso.'
          );
        }
        this.isUploading = false;
        this.persistDraftState();
      },
      error: () => {
        this.isUploading = false;
        this.persistDraftState();
      }
    });
  }

  private resetLoadedCatalogState(): void {
    this.isCatalogLoadedFromServer = false;
    this.loadedCatalogCompanyId = null;
    this.catalogLoadMessage = '';
    this.catalogoProdutosQuestionMapperService.clearCachedAnswerLabels();
  }

  private shouldPreserveRestoredManualMode(routeMode: CatalogMode): boolean {
    if (routeMode !== 'upload') {
      return false;
    }

    return this.mode === 'manual' && this.isCatalogLoadedFromServer && this.manualHasData();
  }

  private extractCatalogItems(
    response: CatalogListResponse | IServerCatalogItem[] | null | undefined
  ): IServerCatalogItem[] {
    if (Array.isArray(response)) {
      return response;
    }

    if (!response || typeof response !== 'object') {
      return [];
    }

    const firstKnownCollection = response.catalog.find((collection) => Array.isArray(collection));
    if (Array.isArray(firstKnownCollection)) {
      return firstKnownCollection;
    }

    const firstArrayValue = Object.values(response).find((value) => Array.isArray(value));
    if (Array.isArray(firstArrayValue)) {
      return firstArrayValue as IServerCatalogItem[];
    }
    return [];
  }

  private mapServerItemToRow(item: IServerCatalogItem): ICatalogRow {
    const ranges = item.categoryRanges || {};

    return {
      itemNumber: this.getFirstFilledValue(item.itemNumber, item.id),
      brandName: this.getFirstFilledValue(item.brandName),
      productName: this.getFirstFilledValue(item.productName, item.name),
      productDescription: this.getFirstFilledValue(item.productDescription, item.description),
      routineInformation: this.mapServerRoutineToRoutineInformation(item.routine ?? item.routineInformation),
      productPhotoLink: this.getFirstFilledValue(item.productPhotoLink, item.photoUrl),
      productLink: this.getFirstFilledValue(item.productLink, item.linkUrl),
      categories: this.mapServerCategoriesToCategory(item.categories),
      productAddToCartCode: this.getFirstFilledValue(item.productAddToCartCode, item.addToCartCode),
      weight: this.mapWeightToString(item.weight ?? item.recommendationWeight),
      price: mapCatalogPriceToString(item.price),
      acne: this.mapRangeToString(ranges.acne ?? item.acne),
      wrinkles: this.mapRangeToString(ranges.wrinkles ?? item.wrinkles),
      redness: this.mapRangeToString(ranges.redness ?? item.redness),
      eyebag: this.mapRangeToString(ranges.eyebag ?? item.eyebag),
      darkCircle: this.mapRangeToString(ranges.darkCircle ?? ranges.darkcircle ?? item.darkCircle ?? item.darkcircle),
      oiliness: this.mapRangeToString(ranges.oiliness ?? item.oiliness),
      answersByQuestionId: this.catalogoProdutosQuestionMapperService.mapServerAnswersByQuestionId(item, this.questions)
    };
  }

  private resetPreview(): void {
    this.previewHeaders = [];
    this.previewColumns = [];
    this.previewRows = [];
    this.previewTotalRows = 0;
    this.previewMessage = '';
  }

  private applyFileProcessingResult(result: ICatalogFileProcessingResult): void {
    this.previewHeaders = result.previewHeaders;
    this.previewColumns = result.previewColumns;
    this.previewRows = result.previewRows;
    this.previewTotalRows = result.previewTotalRows;
    this.previewMessage = result.previewMessage;

    if (result.isSupported && this.mode === 'upload') {
      this.importedRows = result.rows;
      this.selectedRowIndex = null;
      this.isCreatingProduct = false;
      this.updatePagination();
    }
  }

  get currentRows(): ICatalogRow[] {
    return this.mode === 'manual' ? this.manualRows : this.importedRows;
  }

  get catalogProductCount(): number {
    return this.currentRows.filter((row) => this.rowHasData(row)).length;
  }

  get catalogStatusLabel(): string {
    if (this.catalogSource === 'company') {
      return 'Catálogo da empresa';
    }
    if (this.userActivatedMode && this.mode === 'manual') {
      return 'Preenchendo manualmente';
    }
    if (this.userActivatedMode && this.mode === 'upload') {
      return 'Importando arquivo';
    }
    return 'Sem catálogo';
  }

  get questionsStatusLabel(): string {
    if (!this.questions.length) {
      return 'Sem questionário';
    }
    const companyId = String(this.form.getRawValue().companyId || '');
    const hasCompanySpecific = this.questions.some((question) => question.companyId === companyId);
    return hasCompanySpecific
      ? `Publicado · ${this.questions.length} pergunta(s)`
      : `Padrão global · ${this.questions.length} pergunta(s)`;
  }

  get thresholdStatusLabel(): string {
    return this.thresholdSettings?.isCustom ? 'Configurado' : 'Padrão';
  }

  get kitMatrixStatusLabel(): string {
    if (!this.kitMatrix) {
      return 'Sem kits (usa scoring)';
    }
    return `${this.kitMatrix.kits?.length || 0} kit(s) configurado(s)`;
  }

  get filteredRowIndexes(): number[] {
    const search = this.normalizeProductSearchTerm(this.searchProductName);
    if (!search) {
      return this.currentRows.map((_, index) => index);
    }

    return this.currentRows
      .map((row, index) => ({ row, index }))
      .filter(({ row }) => this.normalizeProductSearchTerm(row.productName).includes(search))
      .map(({ index }) => index);
  }

  get filteredRows(): ICatalogRow[] {
    return this.filteredRowIndexes
      .map((rowIndex) => this.currentRows[rowIndex])
      .filter((row): row is ICatalogRow => Boolean(row));
  }

  get pageStart(): number {
    return this.paginaSelecionada * this.tamanhoPagina;
  }

  get pagedRows(): ICatalogRow[] {
    return this.filteredRows.slice(this.pageStart, this.pageStart + this.tamanhoPagina);
  }

  getRowIndex(pageIndex: number): number {
    return this.filteredRowIndexes[this.pageStart + pageIndex] ?? -1;
  }

  onSearchProductNameChange(value: string): void {
    this.searchProductName = String(value || '');
    this.paginaSelecionada = 0;
    this.updatePagination();
    this.persistDraftState();
  }

  clearProductSearch(): void {
    if (!this.searchProductName) {
      return;
    }
    this.onSearchProductNameChange('');
  }

  setPage(pagina: number): void {
    this.paginaSelecionada = pagina;
    this.persistDraftState();
  }

  setPageSize(tamanho: number): void {
    this.tamanhoPagina = tamanho;
    this.paginaSelecionada = 0;
    this.updatePagination();
    this.persistDraftState();
  }

  private updatePagination(): void {
    const totalRows = this.filteredRows.length || 0;
    this.totalPaginas = Math.max(Math.ceil(totalRows / this.tamanhoPagina), 1);
    if (this.paginaSelecionada >= this.totalPaginas) {
      this.paginaSelecionada = Math.max(this.totalPaginas - 1, 0);
    }
  }

  private normalizeProductSearchTerm(value: string | undefined | null): string {
    return this.normalizeHeader(String(value || ''));
  }

  private createEmptyRow(): ICatalogRow {
    return {
      itemNumber: '',
      brandName: '',
      productName: '',
      productDescription: '',
      routineInformation: '',
      productPhotoLink: '',
      productLink: '',
      categories: '',
      productAddToCartCode: '',
      weight: '',
      price: '',
      acne: '',
      wrinkles: '',
      redness: '',
      eyebag: '',
      darkCircle: '',
      oiliness: '',
      answersByQuestionId: {}
    };
  }

  manualHasData(): boolean {
    return this.manualRows.some((row) => this.rowHasData(row));
  }

  private rowHasData(row: ICatalogRow): boolean {
    return rowHasCatalogData(row);
  }

  private normalizeHeader(value: string): string {
    return normalizeCatalogHeader(value);
  }

  private getFirstFilledValue(...values: Array<string | undefined | null>): string {
    return getFirstFilledCatalogValue(...values);
  }

  getQuestionOptions(baseOptions: string[], selectedValues: string[] | null | undefined): string[] {
    const normalizedSelectedValues = (selectedValues || [])
      .map((value) => String(value || '').trim())
      .filter((value) => value.length > 0);

    return Array.from(new Set([...baseOptions, ...normalizedSelectedValues]));
  }

  getQuestionOptionTexts(question: ISkincareQuestion, selected?: string[] | null): string[] {
    const baseOptionTexts = (question.options || []).map((option) => option.text);

    return this.getQuestionOptions(baseOptionTexts, selected);
  }

  private sortQuestions(questions: ISkincareQuestion[]): ISkincareQuestion[] {
    return (questions || [])
      .map((question) => ({
        ...question,
        options: (question.options || []).slice().sort((optionA, optionB) => optionA.order - optionB.order)
      }))
      .sort((questionA, questionB) => questionA.order - questionB.order);
  }

  private mapServerRoutineToRoutineInformation(value: string[] | string | undefined | null): string {
    return mapCatalogRoutineToRoutineInformation(value);
  }

  private mapServerCategoriesToCategory(value: string[] | string | undefined | null): string {
    return mapCatalogCategoriesToCategory(value);
  }

  private mapWeightToString(value: number | string | undefined | null): string {
    return mapCatalogWeightToString(value);
  }

  private mapRangeToString(value: number[] | string | undefined | null): string {
    return mapCatalogRangeToString(value);
  }

  private collectRangeIssues(rows: ICatalogRow[]): Array<{ index: number; fields: string[] }> {
    const issues: Array<{ index: number; fields: string[] }> = [];
    rows.forEach((row, index) => {
      if (!this.rowHasData(row)) {
        return;
      }
      const fields = this.getRangeInvalidFields(row);
      if (fields.length > 0) {
        issues.push({ index, fields });
      }
    });
    return issues;
  }

  private collectWeightIssues(rows: ICatalogRow[]): number[] {
    const issues: number[] = [];
    rows.forEach((row, index) => {
      if (!this.rowHasData(row)) {
        return;
      }

      if (!this.isWeightValid(row.weight)) {
        issues.push(index);
      }
    });
    return issues;
  }

  private collectPriceIssues(rows: ICatalogRow[]): number[] {
    return rows
      .map((row, index) => (this.rowHasData(row) && !isCatalogPriceValid(row.price) ? index : -1))
      .filter((index) => index >= 0);
  }

  getPriceLabel(value: string): string {
    return getCatalogPriceLabel(value);
  }

  getRangeInvalidFields(row: ICatalogRow): string[] {
    const checks = [
      { key: 'Acne', value: row.acne },
      { key: 'Wrinkles', value: row.wrinkles },
      { key: 'Redness', value: row.redness },
      { key: 'Eyebag', value: row.eyebag },
      { key: 'Dark Circle', value: row.darkCircle },
      { key: 'Oiliness', value: row.oiliness }
    ];

    return checks.filter((item) => !this.isRangeValid(item.value)).map((item) => item.key);
  }

  private isRangeValid(value: string): boolean {
    return isCatalogRangeValid(value);
  }

  isWeightValid(value: string): boolean {
    return isCatalogWeightValid(value, this.minWeight, this.maxWeight);
  }

  getWeightLabel(value: string): string {
    return getCatalogWeightLabel(value);
  }

  getWeightRangeValue(value: string): number {
    return getCatalogWeightRangeValue(value, this.minWeight, this.maxWeight);
  }

  onWeightRangeInput(row: ICatalogRow, event: Event): void {
    const input = event.target as HTMLInputElement;
    row.weight = this.mapWeightToString(input.value);
  }

  clearWeight(row: ICatalogRow): void {
    row.weight = '';
  }
}
